import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ResumeModal from '../displayer/ResumeModal';
import { Button, Icon, Message } from '@fasstech/middle-design-system';
import ErrorContainer from '../displayer/ErrorContainer';
import { useAnalytics } from '@@app/context/analytics-context.js';
import extraStyle from '../../extra/extraStyle';
import { useProjectBlocked } from '../../hooks/useProjectBlocked.js';
import { useSelfcareRelay } from '../../hooks/useSelfcareRelay.js';
import { useSavingStore } from '@@app/stores/savingStore.js';

const ButtonDisplayer = ({
  nextButtonLabel,
  nextButtonIsDisabled = false,
  isSubscribeStep = false,
  fasterFileProcessingLabel,
  onClick,
  loading,
  hidden
}) => (
  <div className={'w-full'}>
    <Button
      data-testid="GENERAL_NEXT_BUTTON"
      className={`font-bold tracking-wide ${hidden && 'hidden'} w-full whitespace-nowrap`}
      htmlType="submit"
      onClick={onClick}
      disabled={nextButtonIsDisabled}
      loading={loading}
    >
      {nextButtonLabel}
    </Button>

    {isSubscribeStep && (
      <p
        className={`hidden: lg:absolute font-light text-sm italic ml-14 ${extraStyle.label} tracking-wide mt-1`}
      >
        {fasterFileProcessingLabel}
      </p>
    )}
  </div>
);

const ButtonContainer = ({
  $onPreviousButtonClick,
  $onNextButtonClick = () => {},
  nextButtonLabel,
  nextButtonIsDisabled,
  nextButtonIsLoading = false,
  displayPreviousButton = true,
  previousButtonIsDisabled = false,
  isFormError = false,
  children,
  isSubscribeStep,
  fasterFileProcessingLabel,
  getMiddleText,
  displayResumeButton,
  nextButtonIsHidden = false,
  errorMessage
}) => {
  const [displayModal, setDisplayModal] = useState(false);
  const { trackButtonClick } = useAnalytics();
  const { isBlocked } = useProjectBlocked();
  const { previousIsBlocked } = useSelfcareRelay();
  const isSaving = useSavingStore(({ pendingSaves }) => pendingSaves > 0);

  const nextIsDisabled = nextButtonIsDisabled || isBlocked || isSaving;
  const previousIsDisabled = previousButtonIsDisabled || isBlocked;
  const showPreviousButton = displayPreviousButton && !previousIsBlocked;

  return (
    <div>
      <div className={'w-full flex flex-row justify-end'}>
        <Message variant="error" className={'mb-0!'} label={errorMessage} />
      </div>
      <div
        className={`flex flex-col-reverse justify-between gap-x-6 my-6 lg:mx-0 lg:flex-row ${!showPreviousButton ? 'lg:flex-row-reverse' : ''} items-center`}
      >
        {showPreviousButton && (
          <div className={'flex justify-center w-full md:w-fit!'}>
            <Button
              data-testid="GENERAL_BACK_BUTTON"
              className={`font-bold w-full whitespace-nowrap ${previousIsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => {
                trackButtonClick({
                  code: 'BACK',
                  label: getMiddleText(
                    'GENERAL_COMMON_TEXTS_BACK_BUTTON_LABEL'
                  )
                });
                $onPreviousButtonClick();
              }}
              size="large"
              variant="secondary"
              disabled={previousIsDisabled}
            >
              {getMiddleText('GENERAL_COMMON_TEXTS_BACK_BUTTON_LABEL')}
            </Button>
          </div>
        )}
        <div
          className={
            'flex w-full lg:w-fit! justify-center mb-6 lg:mb-0 gap-4 flex-col-reverse lg:flex-row'
          }
        >
          {displayResumeButton && (
            <Button
              data-testid="GENERAL_SAVE_FOR_LATER_BUTTON"
              variant="text"
              onClick={() => {
                trackButtonClick({
                  code: 'COME_BACK_LATER',
                  label: getMiddleText(
                    'GENERAL_COMMON_TEXTS_SAVE_FOR_LATER_BUTTON_LABEL'
                  )
                });
                setDisplayModal(true);
              }}
              className="font-bold w-full whitespace-nowrap"
            >
              <div className={'flex items-center'}>
                <Icon name={'clockLater'} className={'mr-2'} />
                {getMiddleText(
                  'GENERAL_COMMON_TEXTS_SAVE_FOR_LATER_BUTTON_LABEL'
                )}
              </div>
            </Button>
          )}
          {children}
          <ErrorContainer
            hasError={isFormError}
            errorTooltipMessage={getMiddleText(
              'GENERAL_COMMON_TEXTS_TAB_ACTION_ERROR_WARNING_DESCRIPTION'
            )}
          >
            <ButtonDisplayer
              nextButtonLabel={nextButtonLabel}
              nextButtonIsDisabled={nextIsDisabled || isFormError}
              isSubscribeStep={isSubscribeStep}
              fasterFileProcessingLabel={fasterFileProcessingLabel}
              loading={nextButtonIsLoading || isSaving}
              onClick={() => {
                trackButtonClick({
                  code: 'CONTINUE',
                  label: nextButtonLabel
                });
                $onNextButtonClick();
              }}
              hidden={nextButtonIsHidden}
            />
          </ErrorContainer>
        </div>

        <ResumeModal
          isDisplayed={displayModal}
          setDisplayModal={setDisplayModal}
          resumeModalTitle={getMiddleText(
            'GENERAL_COMMON_TEXTS_RESUME_LATER_TITLE_LABEL'
          )}
          resumeModalText={getMiddleText(
            'GENERAL_COMMON_TEXTS_RESUME_LATER_CONTENT_LABEL'
          )}
          resumeModalButton={getMiddleText(
            'GENERAL_COMMON_TEXTS_RESUME_LATER_BUTTON_LABEL'
          )}
        />
      </div>
    </div>
  );
};

ButtonContainer.propTypes = {
  children: PropTypes.node,
  $onPreviousButtonClick: PropTypes.func,
  nextButtonLabel: PropTypes.string,
  nextButtonIsDisabled: PropTypes.bool,
  isFormError: PropTypes.bool,
  displayResumeButton: PropTypes.bool,
  customTooltipText: PropTypes.string,
  isSubscribeStep: PropTypes.bool,
  errorTooltipMessage: PropTypes.string,
  getMiddleText: PropTypes.func,
  nextButtonIsHidden: PropTypes.bool
};

ButtonDisplayer.propTypes = {
  nextButtonLabel: PropTypes.string,
  nextButtonIsDisabled: PropTypes.bool,
  isSubscribeStep: PropTypes.bool,
  fasterFileProcessingLabel: PropTypes.string,
  hidden: PropTypes.bool
};

export default ButtonContainer;
